import { unwrapSchema } from './read-meta.js';
function getTypeName(schema) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const def = schema?._def;
    return def?.type ?? def?.typeName;
}
export function isObject(schema) {
    const t = getTypeName(unwrapSchema(schema));
    return t === 'object' || t === 'ZodObject';
}
export function isArray(schema) {
    const t = getTypeName(unwrapSchema(schema));
    return t === 'array' || t === 'ZodArray';
}
export function isString(schema) {
    const t = getTypeName(unwrapSchema(schema));
    return t === 'string' || t === 'ZodString';
}
export function isNumber(schema) {
    const t = getTypeName(unwrapSchema(schema));
    return t === 'number' || t === 'ZodNumber';
}
export function isBoolean(schema) {
    const t = getTypeName(unwrapSchema(schema));
    return t === 'boolean' || t === 'ZodBoolean';
}
export function isEnum(schema) {
    const t = getTypeName(unwrapSchema(schema));
    return t === 'enum' || t === 'ZodEnum' || t === 'nativeEnum' || t === 'ZodNativeEnum';
}
export function isLiteral(schema) {
    const t = getTypeName(unwrapSchema(schema));
    return t === 'literal' || t === 'ZodLiteral';
}
export function isDiscriminatedUnion(schema) {
    const t = getTypeName(unwrapSchema(schema));
    return (t === 'discriminatedUnion' || t === 'ZodDiscriminatedUnion' || t === 'union' || t === 'ZodUnion');
}
export function getObjectShape(schema) {
    const inner = unwrapSchema(schema);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const def = inner._def;
    const shape = typeof def?.shape === 'function' ? def.shape() : def?.shape;
    return (shape ?? {});
}
export function getArrayElement(schema) {
    const inner = unwrapSchema(schema);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const def = inner._def;
    return (def?.element ?? def?.type);
}
export function getEnumValues(schema) {
    const inner = unwrapSchema(schema);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const def = inner._def;
    if (Array.isArray(def?.values))
        return def.values;
    if (def?.entries)
        return Object.keys(def.entries);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const options = inner.options;
    if (Array.isArray(options))
        return options;
    return [];
}
export function getLiteralValue(schema) {
    const inner = unwrapSchema(schema);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const def = inner._def;
    if ('value' in (def ?? {}))
        return def.value;
    if (Array.isArray(def?.values))
        return def.values[0];
    return undefined;
}
export function getDiscriminator(schema) {
    const inner = unwrapSchema(schema);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const def = inner._def;
    return def?.discriminator;
}
export function getDiscriminatedBranches(schema) {
    const inner = unwrapSchema(schema);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const def = inner._def;
    if (Array.isArray(def?.options))
        return def.options;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const opts = inner.options;
    if (Array.isArray(opts))
        return opts;
    return [];
}
/**
 * Walk `schema` down a dot/bracket path to resolve the sub-schema at `name`.
 * Supports `a.b`, `a.0.b`, `a.b.c`.
 */
export function resolveSchemaAt(root, path) {
    if (!path)
        return root;
    const parts = path.split('.').filter(Boolean);
    let current = root;
    for (const part of parts) {
        if (!current)
            return undefined;
        const inner = unwrapSchema(current);
        if (isObject(inner)) {
            const shape = getObjectShape(inner);
            current = shape[part];
            continue;
        }
        if (isArray(inner)) {
            // numeric index → element schema
            current = getArrayElement(inner);
            continue;
        }
        if (isDiscriminatedUnion(inner)) {
            // Without a known active branch we can't resolve — caller handles this.
            return undefined;
        }
        return undefined;
    }
    return current;
}
export function isStringLongish(schema, min = 80) {
    if (!isString(schema))
        return false;
    const inner = unwrapSchema(schema);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const def = inner._def;
    const checks = def?.checks ?? [];
    for (const c of checks) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const anyC = c;
        const kind = anyC?.kind ?? anyC?._zod?.def?.check;
        const value = anyC?.value ?? anyC?._zod?.def?.minimum;
        if (kind === 'min_length' || kind === 'min') {
            if (typeof value === 'number' && value >= min)
                return true;
        }
    }
    return false;
}
export function isStringFormat(schema, fmt) {
    if (!isString(schema))
        return false;
    const inner = unwrapSchema(schema);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const def = inner._def;
    // Zod v4: string formats are separate schemas with def.format
    if (def?.format === fmt)
        return true;
    const checks = def?.checks ?? [];
    for (const c of checks) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const anyC = c;
        const kind = anyC?.kind ?? anyC?._zod?.def?.check ?? anyC?._zod?.def?.format;
        if (kind === fmt)
            return true;
        if (anyC?.format === fmt)
            return true;
    }
    return false;
}
export function isIntNumber(schema) {
    if (!isNumber(schema))
        return false;
    const inner = unwrapSchema(schema);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const def = inner._def;
    const checks = def?.checks ?? [];
    for (const c of checks) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const anyC = c;
        const kind = anyC?.kind ?? anyC?._zod?.def?.check;
        if (kind === 'int' || kind === 'number_format')
            return true;
    }
    return false;
}
export function isStringArray(schema) {
    if (!isArray(schema))
        return false;
    const el = getArrayElement(schema);
    return el !== undefined && isString(el);
}
export function isObjectArray(schema) {
    if (!isArray(schema))
        return false;
    const el = getArrayElement(schema);
    return el !== undefined && isObject(el);
}
export function isSchemaOptional(schema) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const def = schema?._def;
    const t = def?.type ?? def?.typeName;
    return t === 'optional' || t === 'ZodOptional' || t === 'default' || t === 'ZodDefault';
}
//# sourceMappingURL=walk-schema.js.map